'use client';

import {
  cloneElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type ElementType,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { breakpoints } from '@/styles/breakpoints';
import { Icon, type IconName } from './icon';
import styles from './popover.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

type TriggerProps = {
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  'aria-haspopup'?: 'menu' | 'dialog';
  'aria-expanded'?: boolean;
};

type PopoverProps = {
  /** The control that opens the panel. Cloned to receive aria + toggle handlers. */
  trigger: ReactElement<TriggerProps>;
  align?: 'start' | 'end';
  /** Accessible name for the panel (translated). */
  label?: string;
  role?: 'menu' | 'dialog';
  scrim?: boolean;
  /**
   * Below the sm breakpoint, detach the panel from its trigger and render it as a
   * fixed sheet with even viewport side margins (with a backdrop), top-aligned
   * just below the trigger, instead of an anchored dropdown. An anchored panel
   * can't be clamped to the viewport in pure CSS, so a trigger near a screen
   * edge (e.g. a folder card's ⋯ in the left grid column) throws a fixed-width
   * panel out of bounds. Opt in for wide `dialog` panels.
   */
  mobileSheet?: boolean;
  defaultOpen?: boolean;
  width?: number | string;
  /**
   * Pinned slots. With either set, the panel becomes header / scrolling body /
   * footer instead of one flow — a long list scrolls while "Mark all as read"
   * and "View all" stay put. Body scroll is capped by `maxHeight`.
   */
  header?: ReactNode;
  footer?: ReactNode;
  /** Cap for the scrolling body. Defaults to `--popover-max-h`. */
  maxHeight?: number | string;
  /** Children, or a render function receiving a `close` callback (for menu items that dismiss on select). */
  children: ReactNode | ((close: () => void) => ReactNode);
};

/**
 * Light, anchored floating panel — not a centered modal, not a full drawer.
 * Opens from its trigger, dismisses on outside-click and Escape. The desktop
 * default for the profile menu, notification bell, sort dropdown and global
 * search (drawer/sheet take over on mobile).
 */
export function Popover({
  trigger,
  align = 'start',
  label,
  role = 'menu',
  scrim = false,
  mobileSheet = false,
  defaultOpen = false,
  width,
  header,
  footer,
  maxHeight,
  children,
}: PopoverProps) {
  const [open, setOpen] = useState(defaultOpen);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Whether the mobileSheet presentation is live (viewport below sm). The sheet
  // must then render through a PORTAL to document.body: `position: fixed`
  // resolves against the nearest ancestor with a backdrop-filter/transform, and
  // the app header is exactly that (backdrop-filter glass bar) — an in-tree
  // "fixed" sheet pins to the header box instead of the viewport (#609).
  const [sheetActive, setSheetActive] = useState(false);
  useEffect(() => {
    if (!mobileSheet) return;
    const mq = window.matchMedia(`(max-width: ${breakpoints.sm}px)`);
    const sync = () => setSheetActive(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [mobileSheet]);

  // Sheet vertical anchor — TOP-aligned just below the trigger (#616), so the
  // panel keeps spatial continuity with what opened it (the bell → right under
  // the app bar; a mid-page trigger → under that trigger) instead of rising
  // from the bottom. Measured at open time from the trigger's viewport box and
  // written straight to the panel's style (pure DOM sync — no re-render);
  // layout effect so the first paint is already positioned. The max height is
  // the space between the anchored top and the bottom margin + gesture-area
  // inset; the stylesheet's static values are the pre-measure fallback.
  useLayoutEffect(() => {
    if (!open || !sheetActive) return;
    const panel = panelRef.current;
    const box = rootRef.current?.getBoundingClientRect();
    if (!panel || !box) return;
    const top = Math.round(box.bottom + 8);
    panel.style.top = `${top}px`;
    panel.style.maxHeight = `calc(100dvh - ${top}px - var(--s-4) - env(safe-area-inset-bottom, 0px))`;
    return () => {
      panel.style.top = '';
      panel.style.maxHeight = '';
    };
  }, [open, sheetActive]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: globalThis.MouseEvent) => {
      const t = e.target as Node;
      // The panel may be portaled out of the anchor subtree — a click inside it
      // must not count as an outside click.
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onPanelKey = (e: React.KeyboardEvent) => {
    if (role !== 'menu') return;
    const items = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? [],
    );
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      (items[i + 1] || items[0])?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      (items[i - 1] || items[items.length - 1])?.focus();
    }
  };

  const clonedTrigger = cloneElement(trigger, {
    onClick: (e: MouseEvent<HTMLElement>) => {
      trigger.props.onClick?.(e);
      setOpen((o) => !o);
    },
    'aria-haspopup': role === 'menu' ? 'menu' : 'dialog',
    'aria-expanded': open,
  });

  const floating = (
    <>
      {open && (scrim || mobileSheet) ? (
        // mobileSheet without an explicit scrim → backdrop only on mobile, where
        // the panel detaches into a sheet; desktop stays a bare anchored dropdown.
        <div
          className={cx(styles.scrim, !scrim && mobileSheet && styles.scrimMobileOnly)}
          aria-hidden="true"
          onMouseDown={() => setOpen(false)}
        />
      ) : null}
      {open ? (
        <div
          ref={panelRef}
          className={cx(
            styles.panel,
            align === 'end' ? styles.alignEnd : styles.alignStart,
            mobileSheet && styles.mobileSheet,
            Boolean(header || footer) && styles.slotted,
          )}
          role={role}
          aria-label={label}
          style={width ? { width } : undefined}
          onKeyDown={onPanelKey}
        >
          {header ? <div className={styles.slotHeader}>{header}</div> : null}
          {header || footer ? (
            <div
              className={styles.slotBody}
              style={maxHeight ? { maxHeight } : undefined}
            >
              {typeof children === 'function' ? children(() => setOpen(false)) : children}
            </div>
          ) : (
            typeof children === 'function' ? children(() => setOpen(false)) : children
          )}
          {footer ? <div className={styles.slotFooter}>{footer}</div> : null}
        </div>
      ) : null}
    </>
  );

  return (
    <div className={styles.anchor} ref={rootRef}>
      {clonedTrigger}
      {mobileSheet && sheetActive ? createPortal(floating, document.body) : floating}
    </div>
  );
}

type MenuItemProps = Omit<ComponentPropsWithRef<'button'>, 'children'> & {
  /** Render as a different element (e.g. Next `Link`) — receives `href` and the rest of the props. */
  as?: ElementType;
  href?: string;
  leadingIcon?: IconName;
  trailingIcon?: IconName;
  danger?: boolean;
  children: ReactNode;
};

export function MenuItem({
  as,
  leadingIcon,
  trailingIcon,
  danger,
  className,
  children,
  ...rest
}: MenuItemProps) {
  const Comp = as ?? 'button';
  const ownProps = as ? {} : { type: 'button' as const };
  return (
    <Comp
      role="menuitem"
      className={cx(styles.menuItem, danger && styles.isDanger, className)}
      {...ownProps}
      {...rest}
    >
      {leadingIcon ? <Icon name={leadingIcon} size={16} className={styles.menuLead} /> : null}
      <span className={styles.menuText}>{children}</span>
      {trailingIcon ? <Icon name={trailingIcon} size={16} className={styles.menuTrail} /> : null}
    </Comp>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className={styles.menuSectionLabel}>{children}</div>;
}

export function MenuSeparator() {
  return <div className={styles.menuSep} role="separator" />;
}
